// --- React & Redux
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Drawer } from 'antd';
import Button from '@dtci/spellbook/Button';
// --- Components
import { UserForm } from './user-form';
// --- Styles
import './styles.scss';
// --- API & Store
import { datastageApi } from 'datastage/api';
import { useUsersActions } from 'datastage/store/users/useUsers';
// --- Hooks
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useDeleteDialog } from 'datastage/components/Dialogs/dialogs/delete';
// --- Utils
import { getFormValue } from '../../utils/userHelpers';

/**
 * Validates user form data.
 *
 * @param {Object} formData - The form data to validate
 * @param {boolean} isCreate - Whether in create mode
 * @returns {Object} Object containing validation errors
 */
const validateUserForm = (formData, isCreate) => {
    const errors = {};

    if (isCreate) {
        if (!formData?.username?.trim()) {
            errors.username = 'Username is required';
        }
        if (!formData?.spaUserRoleIds || formData.spaUserRoleIds.length === 0) {
            errors.spaUserRoleIds = 'At least one role is required';
        }
    }

    return errors;
};

/**
 * User Drawer Component (Unified Create/Edit)
 *
 * Features:
 * - Renders a drawer UI for creating or editing a user
 * - Handles user creation, update, and delete actions via API
 * - Manages loading and toast notifications for user actions
 * - Resets form and scrolls to top on open
 * - Uses separate visibility state for smooth close animation
 *
 * @returns {JSX.Element}
 */
export const UserDrawer = () => {
    const { drawerMode, selectedUser, userForm } = useSelector((state) => state.users);

    const { updateUsers, clearUserForm } = useUsersActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showSuccessToast, showErrorToast } = useToasts();
    const { openDeleteDialog } = useDeleteDialog();

    // Separate visibility state allows close animation to complete before clearing Redux state
    const [isOpen, setIsOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const isCreate = drawerMode === 'create';

    // Open drawer when drawerMode is set
    useEffect(() => {
        if (drawerMode !== null) {
            setIsOpen(true);
        }
    }, [drawerMode]);

    // Handle drawer close animation completion
    const handleAfterOpenChange = (open) => {
        if (!open && drawerMode !== null) {
            clearUserForm();
            setValidationErrors({});
            updateUsers({ drawerMode: null });
        }
    };

    // Scroll drawer body to top when opened
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                const el = document.querySelector('.ant-drawer-body');
                if (el) el.scrollTop = 0;
            }, 0);
        }
    }, [isOpen]);

    const handleClose = () => {
        setIsOpen(false);
    };

    const triggerRefresh = () => {
        updateUsers({ refreshUsers: true });
    };

    /**
     * Creates a new user via API.
     */
    const handleCreateUser = async () => {
        // Validate form before submitting
        const errors = validateUserForm(userForm, true);
        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        setLoadingTrue();
        try {
            const user = await datastageApi.createUser({
                username: userForm?.username,
                spaUserRoleIds: userForm.spaUserRoleIds.map((role) => role.value),
            });

            if (user?.code) {
                setIsOpen(false);
                showErrorToast(user?.message || 'Failed to create user');
                return;
            }

            // Switch to edit mode for the new user and trigger refresh
            updateUsers({
                drawerMode: 'edit',
                selectedUser: user,
                refreshUsers: true,
            });

            showSuccessToast('User created successfully.');
        } catch (error) {
            setIsOpen(false);
            showErrorToast(error?.message || 'Failed to create user');
        } finally {
            setLoadingFalse();
        }
    };

    /**
     * Updates an existing user via API.
     */
    const handleSaveUser = async () => {
        setLoadingTrue();
        try {
            const res = await datastageApi.updateUser({
                id: selectedUser?.id,
                createdAt: selectedUser?.createdAt,
                modifiedAt: selectedUser?.modifiedAt,
                lastLogin: selectedUser?.lastLogin,
                username: getFormValue(userForm?.username, selectedUser?.username || ''),
                fullName: getFormValue(userForm?.fullName, selectedUser?.fullName || ''),
                ermUserName: selectedUser?.ermUserName || '',
                status:
                    userForm?.status !== '' ? userForm?.status.value : selectedUser?.status,
                rightslineId: getFormValue(
                    userForm?.rightslineId,
                    selectedUser?.rightslineId || '',
                ),
                spaUserRoleIds: userForm?.spaUserRoleIds
                    ? userForm?.spaUserRoleIds.map((role) => role.value)
                    : selectedUser?.spaUserRoleIds ?? [],
                tokenHash: selectedUser?.tokenHash || '',
            });

            if (res?.code) {
                showErrorToast(res?.message || 'Failed to update user.');
                return;
            }

            triggerRefresh();
            setIsOpen(false);
            showSuccessToast('User updated successfully.');
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error updating user.');
            console.error('Unexpected error updating user:', error);
        } finally {
            setLoadingFalse();
        }
    };

    /**
     * Opens delete confirmation dialog and handles user deletion.
     */
    const onOpenDeleteDialog = () => {
        handleClose();
        openDeleteDialog(
            'Yes, Delete',
            [
                'Are you sure you wish to delete this user?',
                'Please note: This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();
                try {
                    const res = await datastageApi.deleteUser(selectedUser.id);

                    if (res?.code) {
                        showErrorToast(res?.message || 'Failed to delete user.');
                        return;
                    }

                    triggerRefresh();
                    showSuccessToast('User deleted successfully.');
                } catch (error) {
                    showErrorToast('Error deleting user.');
                    console.error('Error deleting user:', error);
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    /**
     * Renders the drawer footer based on mode.
     */
    const renderFooter = () => {
        if (isCreate) {
            return (
                <div className="button-row">
                    <Button
                        iconType="fa-solid fa-xmark"
                        labelText="close"
                        styleType="link"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        iconType="fa-solid fa-floppy-disk"
                        labelText="create"
                        styleType="link"
                        onClick={handleCreateUser}
                    >
                        Create User
                    </Button>
                </div>
            );
        }

        return (
            <div className="button-row">
                <Button
                    iconType="fa-solid fa-trash"
                    labelText="delete"
                    styleType="link"
                    onClick={onOpenDeleteDialog}
                >
                    Delete
                </Button>
                <Button
                    iconType="fa-solid fa-floppy-disk"
                    labelText="save"
                    styleType="link"
                    onClick={handleSaveUser}
                >
                    Update User
                </Button>
            </div>
        );
    };

    const drawerClassName = `user-drawer ${isCreate ? 'user-drawer--create' : 'user-drawer--edit'}`;

    return (
        <Drawer
            className={drawerClassName}
            title={isCreate ? 'Create User' : 'Edit User'}
            width={750}
            placement="right"
            open={isOpen}
            onClose={handleClose}
            afterOpenChange={handleAfterOpenChange}
            footer={renderFooter()}
        >
            <UserForm isCreate={isCreate} validationErrors={validationErrors} />
        </Drawer>
    );
};
